import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { DateRange } from 'react-day-picker';
import { PrimeTimesGrid } from './PrimeTimesGrid';
import { PremiumResultPeakCards } from './result-data/PremiumResultPeakCards';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WEEKDAYS, HOUR_BUCKETS, ConversionMetric } from '../hooks/types/primeTimesTypes';
import { transformPrimeTimesToMatrix } from './utils/transformPrimeTimes';
import { conversionMetricValue } from './utils/peakCalculations';

interface ResultDataViewProps {
  metric: ConversionMetric;
  date?: DateRange;
  videoId: string | null;
  metricsEnhanced?: any;
}

export function ResultDataView({ metric, date, videoId, metricsEnhanced }: ResultDataViewProps) {
  useLanguage();
  const primeTimes = metricsEnhanced?.primeTimes;
  const isLoading = metricsEnhanced?.isLoading ?? false;

  // Mesma transformação do modo "Dados Gerais"; aqui aproveitamos os campos de conversão da matriz
  const data = primeTimes ? transformPrimeTimesToMatrix(primeTimes) : null;
  // Gate pela MÉTRICA selecionada: receita ou taxa podem ser zero mesmo havendo conversões
  const metricTotal = data
    ? data.matrix.reduce((sum, cell) => sum + conversionMetricValue(cell, metric), 0)
    : 0;
  const hasConversionData = !!data && metricTotal > 0;

  const emptyMetricLabel = {
    conversions: translateInterfaceText("conversão"),
    conversionRate: 'taxa de conversão',
    revenue: 'receita'
  }[metric];

  const handleExportCSV = () => {
    if (!data) return;

    const csvRows = [
      ['Dia', 'Hora', 'Plays Únicos', 'Conversões', 'Taxa de Conversão (%)', 'Receita (R$)']
        .map((label) => translateInterfaceText(label)).join(',')
    ];

    data.matrix.forEach(cell => {
      const weekday = WEEKDAYS.find(w => w.index === cell.weekday);
      const bucket = HOUR_BUCKETS.find(b => b.index === cell.hourBucket);

      csvRows.push([
        weekday?.fullLabel || '',
        bucket?.label || '',
        (cell.playsUnique || 0).toString(),
        (cell.conversions || 0).toString(),
        (cell.conversionRate || 0).toFixed(2),
        (cell.revenue || 0).toFixed(2)
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `horarios-resultado-${metric}-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('peaks_export_csv', { mode: 'result', metric });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!hasConversionData) {
    return (
      <div className="flex flex-col items-center justify-center" style={{ minHeight: 320 }}>
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#EBF4FF] flex items-center justify-center">
          <Clock className="h-8 w-8 text-[#2979FF]" />
        </div>
        <h3 className="text-lg font-semibold text-[#101828] mb-2">
          {!videoId ? translateInterfaceText("Nenhum vídeo selecionado") : translateInterfaceText("Sem dados de {value0} no período", { value0: (emptyMetricLabel) })}
        </h3>
        <p className="text-[#667085] text-sm max-w-[320px] mx-auto text-center">
          {!videoId
            ? translateInterfaceText("Selecione um vídeo para visualizar os dados de resultado.")
            : translateInterfaceText("Tente ampliar o período, escolher outra métrica ou verifique se há uma integração de conversão configurada.")
          }
        </p>
      </div>
    );
  }

  const metricLabel = {
    conversions: 'Conversão',
    conversionRate: 'Taxa de Conversão',
    revenue: 'Receita'
  }[metric];

  return (
    <div className="space-y-6">
      {/* 3 Cards premium superiores */}
      <PremiumResultPeakCards data={data} metric={metric} />

      {/* Heatmap */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[16px] font-semibold text-[#0F172A]">{translateInterfaceText("Mapa de Calor (Conversão)")}</h3>
            <p className="text-[13px] font-normal text-[#6B7280]">
              {metricLabel} {" "}{translateInterfaceText("por dia e horário")}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="hover:bg-[#F3F7FF] hover:border-[#93C5FD] transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            {translateInterfaceText("Exportar CSV")}</Button>
        </div>

        <div className="p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
          <PrimeTimesGrid data={data} metric={metric} />
        </div>
      </div>

      {/* Info tooltip */}
      <div className="p-4 rounded-lg bg-[#F3F7FF] border border-[#D4E1FF]">
        <p className="text-[13px] font-normal text-[#344054]">
          💡 <span className="font-medium">{translateInterfaceText("Como usar:")}</span> {" "}{translateInterfaceText("As cores indicam horários com")}{' '}
          {metric === 'conversions' ? translateInterfaceText("maior número de conversões")
            : metric === 'conversionRate' ? translateInterfaceText("melhor taxa de conversão")
            : translateInterfaceText("maior receita gerada")}.{' '}
          {translateInterfaceText("Agende ofertas e eventos nesses períodos para maximizar resultados.")}</p>
      </div>
    </div>
  );
}
